import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { dictionaries, type Dictionary } from "./i18n.dict";

export type Locale = "lv" | "en";

export const LOCALES: Array<{ code: Locale; label: string; flag: string }> = [
  { code: "lv", label: "Latviešu", flag: "🇱🇻" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

const STORAGE_KEY = "vinoga_admin_locale";
const DEFAULT_LOCALE: Locale = "lv";

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
};

const LocaleContext = createContext<Ctx | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof localStorage === "undefined") return DEFAULT_LOCALE;
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
    return stored && (stored === "lv" || stored === "en") ? stored : DEFAULT_LOCALE;
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocale(): Ctx {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within a LocaleProvider");
  return ctx;
}

export function useLocaleSwitch() {
  return useLocale();
}

/**
 * Returns a translator function `t(key, fallback)`.
 *
 * - Looks up `key` in the active-locale dictionary.
 * - Falls through to the English dictionary, then to the inline fallback string.
 * - The inline fallback doubles as the key's English text — so calling
 *   `t("products.title", "Products")` always renders something sane even before
 *   a translation lands.
 *
 * Pass keys in dot.notation (`namespace.key`). Add LV entries to lib/i18n.dict.ts.
 */
export function useT() {
  const { locale } = useLocale();
  return useCallback(
    (key: string, fallback: string): string => {
      const localeDict = dictionaries[locale] as Dictionary | undefined;
      if (localeDict && key in localeDict) return localeDict[key]!;
      const en = dictionaries.en as Dictionary;
      if (en && key in en) return en[key]!;
      return fallback;
    },
    [locale]
  );
}
